import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { RootAppNavigationParamList } from '../../../../utils/appNavigationNames';
import { usePublicSharedReports } from '../../../../hooks/api/usePublicSharedReport';
import { getIntl } from '../../../../utils/intl';
import { LambdaInvocationTypes } from '../../../../primary-record-types';
import Alert from '../../../v2BaseComponents/Alert';
import { Linking } from 'react-native';
import { useLogarithmicApproachProgress } from '../../../../hooks/useLogarithmicApproachProgress';
import ProgressBar from '../../../v2BaseComponents/ProgressBar';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import Button from '../../../v2BaseComponents/Button';
import Text from '../../../v2BaseComponents/Text';
import { getSpacing } from '../../../../utils/spacing';
import ErrorBoundary from '../../../v2BaseComponents/ErrorBoundary';
import { useCapturedUrlShareCode } from '../../../../hooks/useUrlAppParams';

const intl = getIntl('shared-report', {
    notFoundHeader: 'Report Not Found',
    notFoundMessage:
        'The report you are looking for does not exist or has been removed.  Be sure to check the URL and try again.',
    processingReport: 'Generating Up-To-Date Report',
    processingReportMessage: 'Sit tight; this process should be finished in less than 30 seconds.',
    reportGenerated: 'Report Generated',
    reportGeneratedMessage:
        'The report has been generated and is ready for viewing.  Click the button below to view the report.',
    view: 'View Report',
    cancelledHeader: 'Report Generation Cancelled',
    cancelledMessage:
        'The report generation was cancelled.  If you would like to still view this report, refresh your browser or revisit the shared link.',
    cancel: 'Cancel',
});

export type SharedReportScreenOwnProps = {};

export type SharedReportScreenProps = SharedReportScreenOwnProps & {
    route: RouteProp<RootAppNavigationParamList, 'shared-report'>;
};

const SharedReportScreen: React.FC<SharedReportScreenProps> = (props) => {
    const [wasCancelled, setWasCancelled] = React.useState(false);
    // This page previously used the "old", non-captured URL param.
    // It has now been updated to the captured URL param.
    // However, the "old" way is still supported for backwards compatibility.
    const capturedShareCode = useCapturedUrlShareCode();
    const shareCodeProp = props.route.params?.shareCode;
    const shareCode = shareCodeProp || capturedShareCode;
    const { response } = usePublicSharedReports({ shareCode: shareCode as string });
    const logApproachProgress = useLogarithmicApproachProgress({
        approximateTimeSeconds: 30,
        isDisabled: response.status !== 'PROCESSING',
    });
    const progressValue = response.status === 'PROCESSING' ? logApproachProgress.progress : 100;

    const progressBar = <ProgressBar progress={progressValue} style={{ width: 300 }} />;

    const renderCancelled = () => {
        return (
            <Stack vertical justify={'center'} align={'center'} flex={1}>
                <Alert type="info" title={intl('cancelledHeader')} message={intl('cancelledMessage')} />
            </Stack>
        );
    };

    const renderNoteFound = (_response: LambdaInvocationTypes.SharedReportNotFound) => {
        return (
            <Stack vertical justify={'center'} align={'center'} flex={1}>
                <Alert type="error" title={intl('notFoundHeader')} message={intl('notFoundMessage')} />
            </Stack>
        );
    };

    const renderGeneratingAndFinished = (title: string, message: string, button: any) => (
        <Stack vertical justify={'center'} align={'center'} flex={1} space={getSpacing(0.5)}>
            <Text variant="h5">{title}</Text>
            {progressBar}
            <Text variant="body-1" alignText="center">
                {message}
            </Text>
            {button}
        </Stack>
    );

    const renderProcessingReport = (_response: LambdaInvocationTypes.SharedReportProcessing) => {
        return renderGeneratingAndFinished(
            intl('processingReport'),
            intl('processingReportMessage'),
            <Stack horizontal fullWidth justify="center" align="center">
                <Stack horizontal style={{ width: 300 }}>
                    <Button
                        onPress={() => setWasCancelled(true)}
                        title={intl('cancel')}
                        variant="alert"
                        icon="XCircle"
                        size="lg"
                        testID="shared-report-cancel-button"
                    />
                </Stack>
            </Stack>,
        );
    };

    const renderGeneratedReport = (response: LambdaInvocationTypes.SharedReportReady) => {
        return renderGeneratingAndFinished(
            intl('reportGenerated'),
            intl('reportGeneratedMessage'),
            <Stack horizontal fullWidth justify="center" align="center">
                <Stack horizontal style={{ width: 300 }}>
                    <Button
                        onPress={() => {
                            Linking.openURL(response.reportUrl);
                        }}
                        title={intl('view')}
                        icon="ExternalLink"
                        size="lg"
                        testID="shared-report-view-button"
                    />
                </Stack>
            </Stack>,
        );
    };

    const renderContent = () => {
        if (wasCancelled) {
            return renderCancelled();
        } else if (response.status === 'NOT_FOUND') {
            return renderNoteFound(response);
        } else if (response.status === 'PROCESSING') {
            return renderProcessingReport(response);
        } else if (response.status === 'COMPLETE') {
            return renderGeneratedReport(response);
        } else {
            return renderNoteFound({ status: 'NOT_FOUND' });
        }
    };

    return (
        <ErrorBoundary>
            <Stack vertical fullHeight fullWidth justify="center" align="center" space={contentStackSpacing}>
                {renderContent()}
            </Stack>
        </ErrorBoundary>
    );
};

export default React.memo(SharedReportScreen);
